import { useTheme } from "@emotion/react";
import { Card, CardContainer } from "../../../../components/Card";
import { FilterDropdown, StatCard } from "./OverviewHelper";
import { formatCurrency, formatNumber } from "../utils/StatUtils";
import { Download, Target } from "lucide-react";
import { renderChartByMetric } from "./RenderedChartMetric";

const OVERVIEW_METRIC_OPTIONS = [
    { value: "financial", label: "Financial Metrics" },
    { value: "creatorGrowth", label: "Creator Growth" },
    { value: "engagement", label: "Engagement" },
    { value: "toolUsage", label: "Tool Usage" },
    { value: "conversions", label: "Conversions" },
    { value: "payments", label: "Payments" },
    { value: "targetAchievements", label: "Target Achievement" },
];

const CUMULATIVE_FIELDS = [
    "collabs_billed",
    "affiliate_billed",
    "scaled_billed",
    "app_downloads",
    "total_auto_paid",
    "total_manual_paid",
    "total_signed_creators",
    "total_affiliates",
    "total_creator_pros",
    "total_active_creators",
    "stores_viewed",
    "stores_created",
    "collabs_viewed",
    "collabs_engaged",
    "daily_visits",
    "linkit_visits",
    "qr_visits",
    "score_visits",
    "store_conversion",
    "collab_conversion",
    "daily_conversion",
    "linkit_conversion",
    "collabs_target",
    "scaled_target",
    "affiliate_target",
    "total_earners",
];

const OverviewFilters = ({
    filteredData = [],
    filterBy,
    setFilterBy,
    selectedMetric,
    setSelectedMetric,
}) => {
    const theme = useTheme();

    // Generate cumulative data
    const getCumulativeData = () => {
        const cumulative = [];
        const runningTotals = {};

        filteredData.forEach((week) => {
            const cumulativeWeek = { ...week };
            CUMULATIVE_FIELDS.forEach((field) => {
                runningTotals[field] =
                    (runningTotals[field] || 0) + (week[field] || 0);
                cumulativeWeek[`${field}_Cumulative`] = runningTotals[field];
            });
            cumulative.push(cumulativeWeek);
        });

        return cumulative;
    };

    const dataToUse =
        filterBy === "cumulativeView" ? getCumulativeData() : filteredData;

    const totalCollabsBilled = dataToUse.reduce(
        (sum, item) => sum + (item?.collabs_billed || 0),
        0
    );
    const totalAppDownloads = dataToUse.reduce(
        (sum, item) => sum + (item?.app_downloads || 0),
        0
    );
    const totalEarners = dataToUse.reduce(
        (sum, item) => sum + (item?.total_earners || 0),
        0
    );
    const totalCollabsTarget = dataToUse.reduce(
        (sum, item) => sum + (item?.collabs_target || 0),
        0
    );

    const statSummary = [
        {
            key: "collabs_billed",
            label: "Total Overall Revenue",
            color: theme.palette.graph.cyan,
            value: formatCurrency(totalCollabsBilled),
        },
        {
            key: "app_downloads",
            label: "App Downloads",
            color: theme.palette.graph.magenta,
            icon: <Download className="w-6 h-6" />,
            value: formatNumber(totalAppDownloads),
        },
        {
            key: "total_earners",
            label: "Total Earners",
            color: theme.palette.graph.green,
            icon: <Target className="w-6 h-6" />,
            value: formatNumber(totalEarners),
        },
    ];

    const performanceMetrics = [
        {
            label: "Monthly Target",
            value: formatCurrency(totalCollabsTarget),
            color: theme.palette.graph.cyan,
        },
        {
            label: "Weekly Billed",
            value: formatCurrency(totalCollabsBilled),
            color: theme.palette.graph.yellow,
        },
        {
            label: "Target Achievement",
            value:
                totalCollabsTarget > 0
                    ? `${((totalCollabsBilled / totalCollabsTarget) * 100).toFixed(1)}%`
                    : "0%",
            color: theme.palette.graph.magenta,
        },
        {
            label: "Total Earners",
            value: formatNumber(totalEarners),
            color: theme.palette.graph.green,
        },
    ];

    const filterOptions = [
        {
            label: "Filter By",
            value: filterBy,
            onChange: (e) => setFilterBy(e.target.value),
            options: [
                { value: "weeklyView", label: "Weekly View" },
                { value: "cumulativeView", label: "Cumulative View" },
            ],
        },
        {
            label: "Metric",
            value: selectedMetric,
            onChange: (e) => setSelectedMetric(e.target.value),
            options: OVERVIEW_METRIC_OPTIONS,
        },
    ];

    return (
        <div style={{ marginTop: 35 }}>
            {/* Dropdowns */}
            <div
                className="header-section"
                style={{
                    display: "flex",
                    gap: 40,
                    alignItems: "center",
                }}
            >
                {filterOptions.map((dropdown, idx) => (
                    <FilterDropdown key={idx} {...dropdown} />
                ))}
            </div>

            {/* Stat Cards */}
            <CardContainer>
                {statSummary.map((stat, idx) => (
                    <Card key={idx}>
                        <StatCard {...stat} />
                    </Card>
                ))}
            </CardContainer>

            {/* Chart Section */}
            {renderChartByMetric(dataToUse, filterBy, selectedMetric)}

            {/* Performance Metrics */}
            <CardContainer>
                <Card>
                    <h4>Performance Data</h4>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "1rem",
                            minHeight: "8rem",
                        }}
                    >
                        {performanceMetrics.map((item, idx) => (
                            <div key={idx} style={{ flex: 1 }}>
                                <StatCard {...item} />
                            </div>
                        ))}
                    </div>
                </Card>
            </CardContainer>
        </div>
    );
};

export default OverviewFilters;
